import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface CheckboxOption {
  value: string;
  label: string;
}

@customElement('multi-select-checkbox-field')
export class MultiSelectCheckboxField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: Object }) value: Record<string, boolean> = {};
  @property({ type: Array }) options: CheckboxOption[] = [];
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;

  @state() private _isOpen = false;
  @state() private _searchTerm = '';

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 16px;
      position: relative;
    }
    .field-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: #1e293b;
      font-size: 14px;
    }
    .required {
      color: #ef4444;
    }
    .dropdown-container {
      position: relative;
    }
    .dropdown-trigger {
      width: 100%;
      padding: 10px 36px 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      font-size: 14px;
      background: #f9fafb;
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease;
      position: relative;
      min-height: 42px;
      display: flex;
      align-items: center;
    }
    .dropdown-trigger:hover {
      background: #f3f4f6;
      border-color: #d1d5db;
    }
    .dropdown-trigger.open {
      background: white;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    .dropdown-arrow {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      transition: transform 0.2s;
      pointer-events: none;
      color: #6b7280;
      font-size: 12px;
    }
    .dropdown-arrow.open {
      transform: translateY(-50%) rotate(180deg);
      color: #3b82f6;
    }
    .selected-items {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
    }
    .selected-chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      background: #eff6ff;
      color: #1d4ed8;
      border-radius: 9999px;
      font-size: 13px;
      font-weight: 500;
      border: 1px solid #bfdbfe;
    }
    .selected-chip::before {
      content: '✓';
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 9999px;
      background: #3b82f6;
      color: #ffffff;
      font-size: 12px;
      line-height: 1;
    }
    .placeholder {
      color: #9ca3af;
      font-size: 14px;
    }
    .dropdown-menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
      max-height: 280px;
      overflow-y: auto;
      z-index: 1000;
      animation: dropdownIn 0.15s ease-out;
    }
    @keyframes dropdownIn {
      from {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    .search-box {
      padding: 10px;
      border-bottom: 1px solid #f3f4f6;
      position: sticky;
      top: 0;
      background: white;
      z-index: 1;
    }
    .search-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      font-size: 13px;
      box-sizing: border-box;
      background-color: transparent;
      color: black;
      transition: all 0.15s;
    }
    .search-input:hover {
      background: #f3f4f6;
      border-color: #d1d5db;
    }
    .search-input:focus {
      outline: none;
      background: white;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    .options-wrap {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding: 12px;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      border-radius: 9999px;
      border: 1px solid #e5e7eb;
      background: #ffffff;
      color: #374151;
      cursor: pointer;
      user-select: none;
      transition: background 0.15s, border-color 0.15s, transform 0.05s;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;
    }
    .pill:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }
    .pill:active {
      transform: scale(0.99);
    }
    .pill.selected {
      background: #eff6ff;
      border-color: #93c5fd;
      color: #1d4ed8;
    }
    .pill-check {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 6px;
      border: 2px solid #cbd5e1;
      background: #ffffff;
      box-sizing: border-box;
      transition: border-color 0.15s, background 0.15s;
    }
    .pill.selected .pill-check {
      border-color: #3b82f6;
      background: #3b82f6;
    }
    .pill.selected .pill-check::after {
      content: '';
      width: 8px;
      height: 4px;
      border-left: 2px solid #ffffff;
      border-bottom: 2px solid #ffffff;
      transform: rotate(-45deg);
      margin-top: -1px;
    }
    .pill-label {
      color: inherit;
    }
    .error-message {
      color: #ef4444;
      font-size: 12px;
      margin-top: 6px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .error-message::before {
      content: '⚠';
      font-size: 14px;
    }
  `;

  private _boundClickOutside?: (e: Event) => void;

  connectedCallback() {
    super.connectedCallback();
    this._boundClickOutside = this._handleClickOutside.bind(this);
    document.addEventListener('click', this._boundClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._boundClickOutside) {
      document.removeEventListener('click', this._boundClickOutside);
      this._boundClickOutside = undefined;
    }
  }

  private _handleClickOutside(e: Event) {
    const path = e.composedPath();
    if (!path.includes(this)) {
      this._isOpen = false;
    }
  }

  private _toggleDropdown() {
    this._isOpen = !this._isOpen;
    if (!this._isOpen) {
      this._searchTerm = '';
    }
  }

  private _handleToggle(optionValue: string) {
    const newValue = {
      ...this.value,
      [optionValue]: !this.value[optionValue],
    };
    this.value = newValue;
    this._emitChange();
  }

  private _handleSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    this._searchTerm = input.value.toLowerCase();
  }

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _getFilteredOptions() {
    if (!this._searchTerm) return this.options;
    return this.options.filter((opt) =>
      opt.label.toLowerCase().includes(this._searchTerm)
    );
  }

  private _getLabelForValue(optionValue: string): string {
    const option = this.options.find((opt) => opt.value === optionValue);
    return option?.label || optionValue;
  }

  private _getSelectedValues(): string[] {
    return Object.keys(this.value).filter((key) => this.value[key]);
  }

  render() {
    const filteredOptions = this._getFilteredOptions();
    const selectedValues = this._getSelectedValues();

    return html`
      <div class="field-group">
        ${this.label
          ? html`
              <label class="field-label">
                ${this.label}
                ${this.required ? html`<span class="required">*</span>` : ''}
              </label>
            `
          : ''}
        <div class="dropdown-container">
          <button
            type="button"
            class="dropdown-trigger ${this._isOpen ? 'open' : ''}"
            @click=${this._toggleDropdown}
            @blur=${this._handleBlur}>
            ${selectedValues.length > 0
              ? html`
                  <div class="selected-items">
                    ${selectedValues.map(
                      (val) => html`
                        <span class="selected-chip">
                          ${this._getLabelForValue(val)}
                        </span>
                      `
                    )}
                  </div>
                `
              : html`<span class="placeholder">Select options...</span>`}
            <span class="dropdown-arrow ${this._isOpen ? 'open' : ''}">▼</span>
          </button>

          ${this._isOpen
            ? html`
                <div class="dropdown-menu">
                  <div class="search-box">
                    <input
                      type="text"
                      class="search-input"
                      placeholder="Search..."
                      .value=${this._searchTerm}
                      @input=${this._handleSearch}
                      @click=${(e: Event) => e.stopPropagation()} />
                  </div>
                  <div class="options-wrap">
                    ${filteredOptions.map((option) => {
                      const isSelected = !!this.value[option.value];
                      return html`
                        <button
                          type="button"
                          class="pill ${isSelected ? 'selected' : ''}"
                          @click=${() => this._handleToggle(option.value)}
                          aria-pressed=${isSelected}
                          role="checkbox"
                          aria-checked=${isSelected}>
                          <span class="pill-check" aria-hidden="true"></span>
                          <span class="pill-label">${option.label}</span>
                        </button>
                      `;
                    })}
                  </div>
                </div>
              `
            : ''}
        </div>
        ${this.error && this.touched
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'multi-select-checkbox-field': MultiSelectCheckboxField;
  }
}
